/**
 * 動画スライド生成モジュール用の型定義と定数。
 *
 * カーセンサー物件動画仕様 (マニュアル p.16):
 *   - ファイル: 150 MB 未満、MP4 (H.264 限定) / MPEG / MOV
 *   - 解像度・長さに明示的な上限なし (サイズ 150MB を守れば自由)
 *   - 無音 OK (BGM は著作権 NG なので無音を推奨)
 *
 * HI-TOP Photo Prep Wave 16 仕様 (田中さん承認):
 *   - アスペクト比: 4:3 (1920×1440) — 入力画像そのままの比率
 *   - 1 枚あたり 2 秒表示、ヒーロー (01_hero.png) だけ 4 秒
 *   - クロスフェード 0.3 秒 (xfade フィルタ)
 *   - HI-TOP AUTO TREND ロゴを全編右下に重ねる
 *   - 30 fps、無音、H.264 (yuv420p、CRF 23 デフォルト)
 */

/** 動画の出力解像度 (4:3 固定)。
 *
 * Wave 20: 1920×1440 → 1280×960 に縮小。田中さん本番テストで 77 枚の
 * xfade チェーンが ffmpeg.wasm の WebAssembly メモリ上限 (2GB) を超えて
 * "Out of memory" でエンコード失敗。解像度を 44% 落とすことで中間フレーム
 * サイズを 1/2.25 に削減してメモリ逼迫を回避。1280×960 はカーセンサー
 * 推奨解像度 (1280×960 以上) の下限。入力画像も事前に縮小して入れるため
 * 実質の画質劣化は軽微。
 */
export const VIDEO_SPEC = {
  WIDTH: 1280,
  HEIGHT: 960,
  FPS: 30,
  HERO_DURATION_SEC: 4,
  DEFAULT_DURATION_SEC: 2,
  CROSSFADE_SEC: 0.3,
  /** ロゴ右下余白 (px) */
  LOGO_MARGIN_PX: 20,
  /** ロゴ幅 (動画幅に対する比率)。1280 × 0.12 = 約 154px */
  LOGO_WIDTH_RATIO: 0.12,
  /**
   * Wave 20: ffmpeg に渡す前に画像をこのサイズ以下に縮小する (メモリ削減)。
   * 1280×960 4:3 で、元の 2560×1920 ZIP 画像から Canvas で縮小してから投入。
   */
  PRE_SCALE_WIDTH: 1280,
  PRE_SCALE_HEIGHT: 960,
  /** H.264 CRF (値が小さいほど高品質・大きいほど圧縮)。23 は画質/サイズのバランス点 */
  DEFAULT_CRF: 23,
  /** カーセンサー上限 150MB を意識したフォールバック CRF */
  FALLBACK_CRF: 28,
  /** カーセンサーの絶対上限 */
  MAX_FILE_SIZE_BYTES: 150 * 1024 * 1024,
} as const;

/**
 * 動画を構成する 1 枚のスライドの記述。
 * 入力画像は呼び出し側が Blob で用意し、ffmpeg 側ではインデックス参照する。
 */
export interface VideoSlide {
  /** スライドの Blob (image/jpeg または image/png) */
  readonly blob: Blob;
  /** 表示時間 (秒)。ヒーローは 4、他は 2 が典型 */
  readonly durationSec: number;
  /** デバッグ用ラベル (ログ出力時に使用)。"01_hero" など */
  readonly label: string;
}

/**
 * 動画生成リクエスト。
 *
 * slides[0] が先頭 (ヒーロー画像)、配列順がそのまま動画のシーケンス順。
 * logo は任意 (undefined なら重ねない)。
 */
export interface BuildVideoRequest {
  readonly slides: readonly VideoSlide[];
  readonly logo?: Blob;
  readonly crossfadeSec?: number;
  readonly crf?: number;
  /** 出力ファイル名 (拡張子 .mp4 含む) */
  readonly outputName: string;
}

/**
 * 動画生成の進捗通知。
 */
export interface VideoProgress {
  readonly phase: 'loading-ffmpeg' | 'writing-inputs' | 'encoding' | 'reading-output' | 'done';
  /** 0.0 〜 1.0。フェーズ内の進捗 */
  readonly ratio: number;
  /** 現在のメッセージ (UI 表示用) */
  readonly message?: string;
}

/**
 * 動画生成結果。
 */
export interface BuildVideoResult {
  readonly blob: Blob;
  readonly mimeType: 'video/mp4';
  readonly width: number;
  readonly height: number;
  readonly durationSec: number;
  readonly fileSizeBytes: number;
  readonly elapsedMs: number;
}
